import {
  ArrowLeft,
  ArrowRight,
  Check,
  Zap,
  Shield,
  Clock,
  Image,
  Layers,
  FileJson,
  Package,
  Eye,
  BarChart3,
} from "lucide-react";
import { NavLink } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SectionHeading } from "@/components/sections/SectionHeading";
import { cn } from "@/lib/utils";

const FOOTWEAR_STUDIO_URL = "/footwear-studio";

const Footwear = () => {
  const handleCTAClick = (label: string, section: string) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "footwear_cta_click", {
        cta_label: label,
        section: section,
      });
    }
  };

  return (
    <div className="relative overflow-hidden">
      {/* Decorative background */}
      <div className="pointer-events-none absolute -top-32 left-1/2 h-[360px] w-[360px] -translate-x-1/2 rounded-full bg-stronom-design/25 blur-[180px]" />
      <div className="pointer-events-none absolute -bottom-40 right-[-15%] h-[320px] w-[320px] rounded-full bg-stronom-build/20 blur-[200px]" />

      {/* Hero Section */}
      <section className="relative px-6 pt-20 pb-24">
        <div className="mx-auto max-w-4xl space-y-10">
          <Button
            variant="ghost"
            className="px-0 text-sm text-muted-foreground hover:text-foreground"
            asChild
          >
            <NavLink to="/design" aria-label="Back to STRONOM Design">
              <ArrowLeft className="mr-2 size-4" /> Back to STRONOM Design
            </NavLink>
          </Button>

          <div className="space-y-5">
            <p className="text-xs font-semibold uppercase tracking-[0.45em] text-stronom-design/70">
              STRONOM Design Studio
            </p>
            <h1 className="font-display text-4xl font-semibold text-foreground md:text-5xl">
              Footwear Studio
            </h1>
            <p className="text-lg text-muted-foreground">
              Sketch → Pre-Filters → Rules → Photoreal angles + structured JSON
              for PLM/ERP/CMS. Deterministic parameter per version and full
              audit trail.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <Button
              size="lg"
              className="shadow-[0_18px_60px_hsl(var(--color-design)_/_0.35)]"
              asChild
              onClick={() => handleCTAClick("Request access", "hero")}
            >
              <a
                href={FOOTWEAR_STUDIO_URL}
                target="_blank"
                rel="noreferrer"
                aria-label="Request access to STRONOM Footwear Studio"
              >
                Request access
                <ArrowRight className="ml-2 size-4" />
              </a>
            </Button>
            <Button
              variant="ghost"
              className="text-primary hover:text-primary/80"
              asChild
              aria-label="See how it works - scroll to how it works section"
            >
              <a href="#how-it-works">See how it works</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Key Outcomes */}
      <section className="relative px-6 py-20">
        <div className="mx-auto max-w-6xl space-y-12">
          <SectionHeading
            eyebrow="Key Outcomes"
            title="Why Footwear Studio"
            align="center"
          />

          <div className="grid gap-6 md:grid-cols-3">
            {/* Consistency Card */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-8 backdrop-blur transition-all hover:border-primary/40 hover:bg-card/70">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-stronom-design/20">
                <Check className="size-6 text-stronom-design" />
              </div>
              <h3 className="mb-3 text-lg font-semibold text-foreground">
                Consistency
              </h3>
              <p className="text-sm text-muted-foreground">
                Match designer intent (silhouette, proportions, materials,
                colorways).
              </p>
            </div>

            {/* Speed Card */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-8 backdrop-blur transition-all hover:border-primary/40 hover:bg-card/70">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-stronom-design/20">
                <Zap className="size-6 text-stronom-design" />
              </div>
              <h3 className="mb-3 text-lg font-semibold text-foreground">
                Speed
              </h3>
              <p className="text-sm text-muted-foreground">
                Full angle set in minutes (outside, inside, toe, heel, top,
                outsole, 3/4 L/R).
              </p>
            </div>

            {/* Production Utility Card */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-8 backdrop-blur transition-all hover:border-primary/40 hover:bg-card/70">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-stronom-design/20">
                <Package className="size-6 text-stronom-design" />
              </div>
              <h3 className="mb-3 text-lg font-semibold text-foreground">
                Production Utility
              </h3>
              <p className="text-sm text-muted-foreground">
                Factory-ready outputs: Measurements/Annotations JSON,
                layered/vector assets.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative px-6 py-20">
        <div className="mx-auto max-w-6xl space-y-12">
          <SectionHeading
            eyebrow="Process"
            title="How It Works"
            description="Three steps from sketch to production-ready exports"
            align="center"
          />

          <div className="grid gap-8 md:grid-cols-3">
            {/* Step 1 */}
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-stronom-design/20 text-sm font-semibold text-stronom-design">
                  1
                </div>
                <div className="space-y-2 flex-1">
                  <h3 className="font-semibold text-foreground">
                    Collection Plan & Pre-Filters
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Set season/drop, collection, style name/ID, materials,
                    construction, branding.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-stronom-design/20 text-sm font-semibold text-stronom-design">
                  2
                </div>
                <div className="space-y-2 flex-1">
                  <h3 className="font-semibold text-foreground">
                    Rules & Generation
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    4-layer Rules (Semantics • Technical • Operational •
                    Conformance) govern multi-provider rendering.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-stronom-design/20 text-sm font-semibold text-stronom-design">
                  3
                </div>
                <div className="space-y-2 flex-1">
                  <h3 className="font-semibold text-foreground">
                    Review & Export
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Region micro-corrections, auto-fix suggestions, then export
                    assets + JSON.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center pt-8">
            <Button
              size="lg"
              asChild
              onClick={() => handleCTAClick("Request access", "how-it-works")}
            >
              <a
                href={FOOTWEAR_STUDIO_URL}
                target="_blank"
                rel="noreferrer"
                aria-label="Request access to STRONOM Footwear Studio"
              >
                Request access
                <ArrowRight className="ml-2 size-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="relative px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHeading
            eyebrow="Capabilities"
            title="What You Get"
            align="left"
            className="mb-12"
          />

          <div className="grid gap-12 md:grid-cols-2">
            {/* Left: Bullets */}
            <div className="space-y-4">
              <div className="flex gap-3">
                <Check className="h-5 w-5 flex-shrink-0 text-stronom-design mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">
                    Photoreal multi-angle renders
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Outside, inside, toe, heel, top, outsole, 3/4 L/R
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Check className="h-5 w-5 flex-shrink-0 text-stronom-design mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">
                    Rules engine (4 layers)
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Semantics & Context / Technical & Structural / Operational
                    Compatibility / Conformance & Policies
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Check className="h-5 w-5 flex-shrink-0 text-stronom-design mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Review tools</p>
                  <p className="text-sm text-muted-foreground">
                    A/B & overlay, region micro-prompts, before/after diffs,
                    auto-fixes from Rules
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Check className="h-5 w-5 flex-shrink-0 text-stronom-design mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Exports</p>
                  <p className="text-sm text-muted-foreground">
                    PNG/TIFF, layered PSD/XCF, SVG overlay
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Check className="h-5 w-5 flex-shrink-0 text-stronom-design mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">
                    Annotations & Measurements JSON
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Materials/colors/stitches/hardware + ball width @160 mm,
                    heel/vamp/counter, throat width, strap lengths
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Check className="h-5 w-5 flex-shrink-0 text-stronom-design mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">
                    Deterministic parameter per version
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Full audit (logs, versions, timings)
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Gallery placeholder */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-8 backdrop-blur flex items-center justify-center min-h-[400px]">
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <Image className="h-12 w-12 text-stronom-design/40" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Gallery grid of 4–6 angle thumbnails
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 flex justify-center">
            <Button
              size="lg"
              asChild
              onClick={() => handleCTAClick("Request access", "what-you-get")}
            >
              <a
                href={FOOTWEAR_STUDIO_URL}
                target="_blank"
                rel="noreferrer"
                aria-label="Request access to STRONOM Footwear Studio"
              >
                Request access
                <ArrowRight className="ml-2 size-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Pre-Filters & Libraries */}
      <section className="relative px-6 py-20">
        <div className="mx-auto max-w-6xl space-y-12">
          <SectionHeading
            eyebrow="Configuration"
            title="Pre-Filters & Libraries"
            align="center"
          />

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {/* Card 1 */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-6 backdrop-blur space-y-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-stronom-design/20">
                <Eye className="size-5 text-stronom-design" />
              </div>
              <h3 className="font-semibold text-foreground text-sm">
                Pre-Filters
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Type & category; last & fit; outsole/heel; materials;
                construction; stitching; hardware; colorway; branding;
                compliance.
              </p>
            </div>

            {/* Card 2 */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-6 backdrop-blur space-y-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-stronom-design/20">
                <Layers className="size-5 text-stronom-design" />
              </div>
              <h3 className="font-semibold text-foreground text-sm">
                Season/Drop & Style
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Season, drop, collection; style name/ID; brand logo; presets per
                brand.
              </p>
            </div>

            {/* Card 3 */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-6 backdrop-blur space-y-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-stronom-design/20">
                <Package className="size-5 text-stronom-design" />
              </div>
              <h3 className="font-semibold text-foreground text-sm">
                Libraries
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Last, outsole/heel, materials (PBR), brand DNA packs, compliance
                packs.
              </p>
            </div>

            {/* Card 4 */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-6 backdrop-blur space-y-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-stronom-design/20">
                <Shield className="size-5 text-stronom-design" />
              </div>
              <h3 className="font-semibold text-foreground text-sm">
                Determinism Policy
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Org-level policy for deterministic reruns and versioned Rules.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Gates */}
      <section className="relative px-6 py-20">
        <div className="mx-auto max-w-6xl space-y-12">
          <SectionHeading
            eyebrow="Standards"
            title="Quality Gates"
            align="center"
          />

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {/* Metric 1 */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-6 backdrop-blur space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-stronom-design/70">
                Silhouette Match
              </p>
              <p className="text-2xl font-semibold text-foreground">
                IoU ≥ 0.97
              </p>
              <p className="text-xs text-muted-foreground">
                Hero images / ≥ 0.95 others
              </p>
            </div>

            {/* Metric 2 */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-6 backdrop-blur space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-stronom-design/70">
                Material Fidelity
              </p>
              <p className="text-2xl font-semibold text-foreground">
                Score ≥ 0.9
              </p>
              <p className="text-xs text-muted-foreground">vs exemplars</p>
            </div>

            {/* Metric 3 */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-6 backdrop-blur space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-stronom-design/70">
                Rule Compliance
              </p>
              <p className="text-2xl font-semibold text-foreground">≥ 95%</p>
              <p className="text-xs text-muted-foreground">
                0 critical violations
              </p>
            </div>

            {/* Metric 4 */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-6 backdrop-blur space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-stronom-design/70">
                Time to Set
              </p>
              <p className="text-2xl font-semibold text-foreground">≤ 5 min</p>
              <p className="text-xs text-muted-foreground">
                full angles at scale
              </p>
            </div>
          </div>

          <p className="text-center text-xs text-muted-foreground">
            Targets are configurable by org.{" "}
            <a
              href={FOOTWEAR_STUDIO_URL}
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:text-primary/80 underline"
            >
              Request access
            </a>{" "}
            to learn more.
          </p>
        </div>
      </section>

      {/* Tech Pack Bridges */}
      <section className="relative px-6 py-20">
        <div className="mx-auto max-w-6xl space-y-12">
          <SectionHeading
            eyebrow="Outputs"
            title="Tech Pack Bridges"
            align="center"
          />

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {/* Image Set */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-6 backdrop-blur space-y-3">
              <Image className="h-8 w-8 text-stronom-design" />
              <h3 className="font-semibold text-foreground">Image Set</h3>
              <p className="text-sm text-muted-foreground">
                PNG (transparent & shadow), 300 DPI print, optional TIFF
              </p>
            </div>

            {/* Layered File */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-6 backdrop-blur space-y-3">
              <Layers className="h-8 w-8 text-stronom-design" />
              <h3 className="font-semibold text-foreground">Layered File</h3>
              <p className="text-sm text-muted-foreground">
                PSD/XCF (panels, stitches, hardware)
              </p>
            </div>

            {/* Vector Overlay */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-6 backdrop-blur space-y-3">
              <ArrowRight className="h-8 w-8 text-stronom-design" />
              <h3 className="font-semibold text-foreground">Vector Overlay</h3>
              <p className="text-sm text-muted-foreground">
                SVG (panel IDs, stitch paths)
              </p>
            </div>

            {/* JSON */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-6 backdrop-blur space-y-3">
              <FileJson className="h-8 w-8 text-stronom-design" />
              <h3 className="font-semibold text-foreground">JSON</h3>
              <p className="text-sm text-muted-foreground">
                Annotations & Measurements
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-border/60 bg-card/50 p-8 backdrop-blur">
            <h3 className="font-semibold text-foreground mb-4">
              Export Bundles
            </h3>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">Factory</p>
                <p className="text-xs text-muted-foreground">
                  Production-ready technical specs
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  Merch & Marketing
                </p>
                <p className="text-xs text-muted-foreground">
                  Campaign-ready imagery
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  Internal Review
                </p>
                <p className="text-xs text-muted-foreground">
                  Development and approval assets
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Governance */}
      <section className="relative px-6 py-20">
        <div className="mx-auto max-w-4xl rounded-3xl border border-border/60 bg-card/50 p-8 md:p-12 backdrop-blur">
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-foreground">
              Trust & Governance
            </h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex gap-3">
                <Shield className="h-5 w-5 flex-shrink-0 text-stronom-design mt-0.5" />
                <span className="text-sm text-muted-foreground">
                  Role approvals
                </span>
              </div>
              <div className="flex gap-3">
                <Shield className="h-5 w-5 flex-shrink-0 text-stronom-design mt-0.5" />
                <span className="text-sm text-muted-foreground">
                  Audit logs
                </span>
              </div>
              <div className="flex gap-3">
                <Shield className="h-5 w-5 flex-shrink-0 text-stronom-design mt-0.5" />
                <span className="text-sm text-muted-foreground">
                  Policy packs
                </span>
              </div>
              <div className="flex gap-3">
                <Shield className="h-5 w-5 flex-shrink-0 text-stronom-design mt-0.5" />
                <span className="text-sm text-muted-foreground">
                  Multi-provider rendering
                </span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground border-t border-border/40 pt-6">
              Deterministic parameter per version ensures reproducibility across
              all workflows and audit requirements.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative px-6 py-20">
        <div className="mx-auto max-w-4xl space-y-12">
          <SectionHeading
            eyebrow="FAQ"
            title="Frequently Asked Questions"
            align="center"
          />

          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-left hover:no-underline hover:text-primary">
                <span className="text-foreground">
                  What makes STRONOM different?
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                4-layer Rules, deterministic re-runs, and dual outputs (visuals
                + structured JSON) for PLM/ERP/CMS integration. Every design
                decision is governed by policy and audit-ready.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2">
              <AccordionTrigger className="text-left hover:no-underline hover:text-primary">
                <span className="text-foreground">Do I need 3D?</span>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                No. 2D outputs are factory-ready; we provide bridges to 3D when
                needed. The Studio is designed for designers and product teams
                who work primarily in 2D.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3">
              <AccordionTrigger className="text-left hover:no-underline hover:text-primary">
                <span className="text-foreground">
                  Can I use my brand palettes and libraries?
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Yes. Import palettes, last/outsole libraries, material PBR sets,
                and brand DNA packs. Your libraries stay yours and are versioned
                with your Studio workspace.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4">
              <AccordionTrigger className="text-left hover:no-underline hover:text-primary">
                <span className="text-foreground">
                  Is it reproducible for audits/factories?
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Yes. Deterministic parameter per version, versioned Rules, and
                full execution logs ensure every render can be reproduced
                exactly. Perfect for audit trails and factory handoffs.
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="flex justify-center pt-8">
            <Button
              size="lg"
              asChild
              onClick={() => handleCTAClick("Request access", "faq")}
            >
              <a
                href={FOOTWEAR_STUDIO_URL}
                target="_blank"
                rel="noreferrer"
                aria-label="Request access to STRONOM Footwear Studio"
              >
                Request access
                <ArrowRight className="ml-2 size-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative px-6 py-20">
        <div className="mx-auto max-w-4xl rounded-3xl border border-border/60 bg-gradient-to-br from-stronom-design/15 to-stronom-build/15 p-12 backdrop-blur space-y-6 text-center">
          <h3 className="text-2xl font-semibold text-foreground">
            Ready to try Footwear Studio?
          </h3>
          <p className="text-muted-foreground">
            Start bringing governed 2D design to your footwear pipeline.
          </p>
          <Button
            size="lg"
            asChild
            onClick={() => handleCTAClick("Request access", "final-cta")}
          >
            <a
              href={FOOTWEAR_STUDIO_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="Request access to STRONOM Footwear Studio"
            >
              Request access
              <ArrowRight className="ml-2 size-4" />
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Footwear;
